import { useEffect, useState } from "react";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useComboState } from "#/store/combo/comboState";

import { useButterfly } from "./hooks/useButterfly";
import { useCalendar } from "./hooks/useCalendar";
import { useCovered } from "./hooks/useCovered";
import { useIronCondor } from "./hooks/useIronCondor";
import { useSpread } from "./hooks/useSpread";
import { useStraddle } from "./hooks/useStraddle";
import { useStrangle } from "./hooks/useStrangle";

export type StrategyId =
  | "custom"
  | "covered-call"
  | "covered-put"
  | "call-spread"
  | "put-spread"
  | "call-calendar"
  | "put-calendar"
  | "call-butterfly"
  | "put-butterfly"
  | "straddle"
  | "strangle"
  | "iron-condor";

export interface Strategy {
  id: StrategyId;
  label: string;
  apply?: () => void;
  isActivePattern?: () => boolean;
}

export const useStrategySelectorController = () => {
  const { comboState } = useComboState();
  const { comboPerpInstruments, comboOptionInstruments } =
    useComboInstrumentsController();
  const {
    applyCovered: applyCoveredPut,
    isActivePatternCovered: isActivePatternCoveredPut,
  } = useCovered("put");
  const {
    applyCovered: applyCoveredCall,
    isActivePatternCovered: isActivePatternCoveredCall,
  } = useCovered("call");
  const {
    applyCalendar: applyCallCalendar,
    isActivePatternCalendar: isActivePatternCallCalendar,
  } = useCalendar("call");
  const {
    applyCalendar: applyPutCalendar,
    isActivePatternCalendar: isActivePatternPutCalendar,
  } = useCalendar("put");
  const {
    applySpread: applyCallSpread,
    isActivePatternSpread: isActivePatternCallSpread,
  } = useSpread("call");
  const {
    applySpread: applyPutSpread,
    isActivePatternSpread: isActivePatternPutSpread,
  } = useSpread("put");
  const {
    applyButterfly: applyCallButterfly,
    isActivePatternButterfly: isActivePatternCallButterfly,
  } = useButterfly("call");
  const {
    applyButterfly: applyPutButterfly,
    isActivePatternButterfly: isActivePatternPutButterfly,
  } = useButterfly("put");
  const { applyStraddle, isActivePatternStraddle } = useStraddle();
  const { applyStrangle, isActivePatternStrangle } = useStrangle();
  const { applyIronCondor, isActivePatternIronCondor } = useIronCondor();

  const [strategy, setStrategy] = useState<StrategyId>("custom");

  const handleSelect = (id: StrategyId) => () => {
    if (id === strategy) return;

    setStrategy(id);
    strategies.find((s) => s.id === id)?.apply?.();
  };

  const isComboStatePrices = comboState === "prices";

  const strategies: Strategy[] = [
    { id: "custom", label: "Custom" },
    {
      id: "covered-call",
      label: "Covered Call",
      apply: applyCoveredCall,
      isActivePattern: isActivePatternCoveredCall,
    },
    {
      id: "covered-put",
      label: "Covered Put",
      apply: applyCoveredPut,
      isActivePattern: isActivePatternCoveredPut,
    },
    {
      id: "call-spread",
      label: "Call Spread",
      apply: applyCallSpread,
      isActivePattern: isActivePatternCallSpread,
    },
    {
      id: "put-spread",
      label: "Put Spread",
      apply: applyPutSpread,
      isActivePattern: isActivePatternPutSpread,
    },
    {
      id: "call-calendar",
      label: "Call Calendar",
      apply: applyCallCalendar,
      isActivePattern: isActivePatternCallCalendar,
    },
    {
      id: "put-calendar",
      label: "Put Calendar",
      apply: applyPutCalendar,
      isActivePattern: isActivePatternPutCalendar,
    },
    {
      id: "call-butterfly",
      label: "Call Butterfly",
      apply: applyCallButterfly,
      isActivePattern: isActivePatternCallButterfly,
    },
    {
      id: "put-butterfly",
      label: "Put Butterfly",
      apply: applyPutButterfly,
      isActivePattern: isActivePatternPutButterfly,
    },
    {
      id: "straddle",
      label: "Straddle",
      apply: applyStraddle,
      isActivePattern: isActivePatternStraddle,
    },
    {
      id: "strangle",
      label: "Strangle",
      apply: applyStrangle,
      isActivePattern: isActivePatternStrangle,
    },
    {
      id: "iron-condor",
      label: "Iron Condor",
      apply: applyIronCondor,
      isActivePattern: isActivePatternIronCondor,
    },
  ];

  useEffect(() => {
    const found = strategies.find((s) => s.isActivePattern?.());

    if (found?.id === strategy) return;

    setStrategy(found?.id || "custom");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply strategyId on manual changes
  }, [comboPerpInstruments, comboOptionInstruments]);

  return {
    strategy,
    strategies,
    isComboStatePrices,
    handleSelect,
  };
};
